import React, { useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { ChevronRight, Clock, Search, Scissors, Palette, Sparkles, Heart, Eye, Waves, Phone } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { services, salonInfo } from '../data/mock';

const categoryIcons = {
  'Hair Services': Scissors,
  'Nail Services': Heart,
  'Makeup Services': Palette,
  'Spa & Skincare': Waves,
  'Waxing Services': Sparkles,
  'Lash & Brow': Eye,
};

const ServicesPage = () => {
  const [searchParams] = useSearchParams();
  const [selectedCategory, setSelectedCategory] = useState(searchParams.get('category') || 'all');
  const [searchQuery, setSearchQuery] = useState('');

  const filteredServices = services.filter((service) => {
    if (selectedCategory !== 'all' && service.category !== selectedCategory) {
      return false;
    }
    if (searchQuery) {
      return service.category.toLowerCase().includes(searchQuery.toLowerCase()) ||
        service.items.some(item => item.name.toLowerCase().includes(searchQuery.toLowerCase()));
    }
    return true;
  });

  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section */}
      <section className="relative py-20 px-4 bg-gradient-to-br from-[#EBF4F6] via-white to-[#EBF4F6]/50 overflow-hidden">
        {/* Decorative Background */}
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute top-20 right-[10%] w-80 h-80 bg-gradient-to-br from-[#3D7A8C]/10 to-[#5DA7B7]/10 rounded-full blur-3xl"></div>
          <div className="absolute bottom-10 left-[5%] w-96 h-96 bg-gradient-to-br from-[#86BDC9]/10 to-[#3D7A8C]/10 rounded-full blur-3xl"></div>
        </div>

        <div className="max-w-7xl mx-auto text-center relative z-10">
          <Badge className="mb-6 bg-[#3D7A8C]/10 text-[#3D7A8C] border-0 px-5 py-2 text-sm font-medium">
            Premium Services
          </Badge>
          <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl font-bold mb-6 text-gray-900 tracking-tight">
            Our Services
          </h1>
          <p className="text-lg sm:text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
            Discover our comprehensive menu of luxury beauty services designed to help you look and feel amazing
          </p>
        </div>
      </section>

      <div className="max-w-7xl mx-auto px-4 py-12">
        {/* Search and Filter */}
        <div className="mb-12">
          <div className="relative max-w-xl mx-auto mb-8">
            <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
            <Input
              type="text"
              placeholder="Search services..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-12 pr-4 py-6 text-lg border-2 border-gray-200 focus:border-[#3D7A8C] rounded-xl"
              data-testid="services-search-input"
            />
          </div>

          <div className="flex flex-wrap gap-3 justify-center">
            <Button
              variant={selectedCategory === 'all' ? 'default' : 'outline'}
              onClick={() => setSelectedCategory('all')}
              style={{
                backgroundColor: selectedCategory === 'all' ? '#3D7A8C' : '#ffffff',
                color: selectedCategory === 'all' ? '#ffffff' : '#1f2937',
                borderColor: '#9ca3af',
                borderWidth: '2px',
                fontWeight: '600'
              }}
              data-testid="filter-all"
            >
              All Services
            </Button>
            {services.map((service) => (
              <Button
                key={service.id}
                variant={selectedCategory === service.category ? 'default' : 'outline'}
                onClick={() => setSelectedCategory(service.category)}
                style={{
                  backgroundColor: selectedCategory === service.category ? '#3D7A8C' : '#ffffff',
                  color: selectedCategory === service.category ? '#ffffff' : '#1f2937',
                  borderColor: '#9ca3af',
                  borderWidth: '2px',
                  fontWeight: '600'
                }}
                data-testid={`filter-${service.category.toLowerCase().replace(/\s+/g, '-')}`}
              >
                {service.category}
              </Button>
            ))}
          </div>
        </div>

        {/* Services Grid */}
        <div className="space-y-12">
          {filteredServices.map((service) => (
            <div key={service.id} className="scroll-mt-20">
              <Card className="overflow-hidden border-2 border-gray-100 shadow-xl">
                <div className="grid md:grid-cols-3">
                  <div className="relative h-64 md:h-auto">
                    <div className="absolute inset-0 bg-gradient-to-br from-[#3D7A8C]/60 to-[#356A7A]/50 z-10"></div>
                    <img
                      src={service.image}
                      alt={service.category}
                      className="w-full h-full object-cover"
                    />
                    <div className="absolute inset-0 z-20 flex items-center justify-center p-8 text-center">
                      <div>
                        <h2 className="font-serif text-3xl md:text-4xl font-bold text-white mb-3">
                          {service.category}
                        </h2>
                        <Badge className="bg-white/20 text-white border-white/30 backdrop-blur-sm">
                          {service.items.length} Services Available
                        </Badge>
                      </div>
                    </div>
                  </div>

                  <div className="md:col-span-2 p-8">
                    <div className="space-y-4">
                      {service.items.map((item) => (
                        <div
                          key={`${service.id}-${item.name}`}
                          className="group flex items-start justify-between p-4 rounded-xl hover:bg-[#3D7A8C]/5 transition-all duration-300 border-2 border-gray-100 hover:border-[#3D7A8C]/30"
                        >
                          <div className="flex-1">
                            <h3 className="font-serif text-lg font-bold text-gray-900 mb-2 group-hover:text-[#3D7A8C] transition-colors">
                              {item.name}
                            </h3>
                            <div className="flex flex-wrap gap-4 text-sm">
                              <div className="flex items-center gap-1.5">
                                <Clock className="w-4 h-4 text-[#3D7A8C]" />
                                <span className="font-medium text-gray-700">{item.duration}</span>
                              </div>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </Card>
            </div>
          ))}
        </div>

        {filteredServices.length === 0 && (
          <div className="text-center py-20">
            <p className="text-xl text-gray-600 mb-6">No services found matching your search</p>
            <Button
              onClick={() => {
                setSearchQuery('');
                setSelectedCategory('all');
              }}
              className="bg-[#3D7A8C] hover:bg-[#356A7A] text-white font-semibold"
            >
              Clear Filters
            </Button>
          </div>
        )}

        {/* CTA Section */}
        <div className="mt-16 bg-gradient-to-br from-[#EBF4F6] to-[#D7E9ED] rounded-2xl p-12 text-center border-2 border-[#3D7A8C]/20">
          <h3 className="font-serif text-3xl font-bold text-gray-900 mb-4">
            Have Questions About Our Services?
          </h3>
          <p className="text-lg text-gray-700 mb-8 max-w-2xl mx-auto">
            Our team is happy to help you choose the perfect service. Give us a call or visit us today!
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a href={`tel:${salonInfo.phone}`}>
              <Button size="lg" className="bg-[#3D7A8C] hover:bg-[#356A7A] text-white px-8 shadow-xl hover:shadow-2xl transition-all duration-300 font-semibold">
                <Phone className="mr-2 h-5 w-5" />
                Call Us
              </Button>
            </a>
            <Link to="/location">
              <Button size="lg" variant="outline" className="border-2 border-[#3D7A8C] text-[#3D7A8C] hover:bg-[#3D7A8C]/10 px-8 shadow-lg transition-all duration-300 font-semibold">
                Visit Our Location
                <ChevronRight className="ml-2 h-5 w-5" />
              </Button>
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ServicesPage;
